<!--
	Copyright (c) 2017-present, flop! contributors
	SPDX-License-Identifier: AGPL-3.0-or-later
	https://framagit.org/flopedt/flopedt
-->

<script lang="ts">
import type { DropdownMenuContentEmits, DropdownMenuContentProps } from 'reka-ui'

import { ButtonColor, ButtonLook } from '@uikit/button/SimpleButton.vue'

import './menu.css'

export interface DropdownMenuProps extends DropdownMenuContentProps {
  asChild?: boolean
  /** If `true`, the dropdown arrow icon on the button will not be displayed. No effect if `as-child` is `true`.  */
  noIcon?: boolean
  /** Turns the button into an icon button. No effect if `as-child` is `true`. */
  iconButton?: boolean
  /** ARIA label passed to the button. No effect if `as-child` is `true`. */
  ariaLabel?: string
}

export type DropdownMenuEmits = DropdownMenuContentEmits

type DropdownMenuSlots = {
  trigger?: () => unknown
  items: () => unknown
}
</script>

<script setup lang="ts">
import {
  DropdownMenuArrow,
  DropdownMenuContent,
  DropdownMenuPortal,
  DropdownMenuRoot,
  DropdownMenuTrigger,
} from 'reka-ui'
import { computed } from 'vue'

import { useForwardPropsEmitsSkip } from '@/composables/utils/useForwardPropsEmitsSkip.ts'

import SimpleButton from '@uikit/button/SimpleButton.vue'

import ChevronDown from '~mingcute/arrow/down_line.svg'

const open = defineModel<boolean>('open')
const props = defineProps<DropdownMenuProps>()
const emit = defineEmits<DropdownMenuEmits>()
const slots = defineSlots<DropdownMenuSlots>()

const forwarded = useForwardPropsEmitsSkip(props, emit, ['asChild', 'iconButton', 'ariaLabel'], ['open'])

const isIconButton = computed(() => props.iconButton || !slots.trigger)

if (import.meta.env.DEV) {
  if (props.asChild && props.ariaLabel) {
    console.warn('[DropdownMenu] a11y: Specifying an aria-label has no effect when using as-child!')
  }

  if (!slots.trigger && !props.ariaLabel) {
    console.warn("[DropdownMenu] a11y: Trigger without textual content didn't receive an aria-label!")
  }
}
</script>

<template>
  <DropdownMenuRoot v-model:open="open">
    <DropdownMenuTrigger as-child>
      <SimpleButton
        v-if="!asChild"
        :color="open ? ButtonColor.ACCENT : ButtonColor.PRIMARY"
        :look="ButtonLook.OUTLINED"
        :icon-button="isIconButton"
        :aria-label="ariaLabel"
        class="flopui-menu-dropdown-trigger"
      >
        <slot name="trigger" />
        <ChevronDown v-if="noIcon !== true" class="flopui-menu-dropdown-icon" />
      </SimpleButton>
      <slot v-else name="trigger" />
    </DropdownMenuTrigger>

    <DropdownMenuPortal>
      <DropdownMenuContent v-bind="forwarded" class="flopui-menu-content" :collision-padding="8">
        <div class="flopui-menu-viewport">
          <slot name="items" />
        </div>
        <DropdownMenuArrow class="flopui-menu-arrow" />
      </DropdownMenuContent>
    </DropdownMenuPortal>
  </DropdownMenuRoot>
</template>

<style>
.flopui-menu-dropdown-trigger[data-state='open'] .flopui-menu-dropdown-icon {
  transform: rotate(180deg);
}

@media (prefers-reduced-motion: no-preference) {
  .flopui-menu-dropdown-trigger .flopui-menu-dropdown-icon {
    transition: transform 0.3s;
  }
}
</style>
